import Markdown from "./Markdown"
import { Box, Typography } from "@mui/material"
import Grid from "@mui/material/Grid"
import ProfileSidebar from "components/ProfileSidebar"
import VideosList from "components/VideosList"
import { ActivityIndicator } from "components/activity-indicator"
import AttachmentsHorizontalScroll from "components/attachments/AttachmentsHorizontalScroll"
import TeamMemberHorizontalScroll from "components/team-members/TeamMemberHorizontalScroll"
import ConventionExhibitor from "models/convention-api/v1/ConventionExhibitor"
import ConventionExhibitorProfileAttachment from "models/convention-api/v1/ConventionExhibitorProfileAttachment"
import ConventionTeamMember from "models/convention-api/v1/ConventionTeamMember"
import { makeStyles } from "tss-react/mui-compat"

interface CuratorProps {
  conventionExhibitor: ConventionExhibitor
}

const useStyles = makeStyles()(
  (theme) => ({
    h2: {
      fontFamily: "Raleway",
      fontSize: "24px",
      fontWeight: 600,
      marginTop: "40px",
    },
    h5: {
      fontFamily: "Raleway",
      fontSize: "18px",
      marginBottom: "20px",
      fontWeight: 700,
    },
    content: {
      marginTop: "40px",
      flexDirection: "column-reverse",
      [theme.breakpoints.up(769)]: {
        flexDirection: "row",
      },
    },
    bioSection: {
      width: "70%",
      [theme.breakpoints.down(769)]: {
        width: "100%",
      },
    },
    bio: {
      margin: "20px 0",
      color: "#262B2D",
      fontFamily: "Lato",
      fontSize: "16px",
      letterSpacing: 0,
      lineHeight: "22px",
      [theme.breakpoints.up(769)]: {
        marginRight: "90px",
      },
    },
    btnSection: {
      width: "30%",
      [theme.breakpoints.down(769)]: {
        width: "100%",
        marginBottom: "2rem",
      },
    },
  })
)

const CuratorBio = (props: CuratorProps) => {
  const { conventionExhibitor } = props
  const attachmentParams = {
    limit: 25,
    filter: {
      institution_id: conventionExhibitor.institution_id,
    },
    includeCount: true,
  }

  const teamMembersParams = {
    limit: 25,
    filter: {
      institution_id: conventionExhibitor.institution_id,
    },
    includeCount: true,
  }

  const attachments = ConventionExhibitorProfileAttachment.useMore(attachmentParams)
  const teamMembers = ConventionTeamMember.useMore(teamMembersParams)
  const { classes } = useStyles()

  if (
    !conventionExhibitor ||
    !conventionExhibitor.chat_available ||
    attachments.isLoadingInitial ||
    teamMembers.isLoadingInitial
  ) {
    return <ActivityIndicator />
  }

  return (
    <>
      <Typography variant="h2" className={classes.h2}>
        About {conventionExhibitor.exhibitor_name}
      </Typography>

      <Grid container className={classes.content}>
        {conventionExhibitor.exhibitor_bio && (
          <Grid item className={classes.bioSection}>
            <Typography variant="h5" className={classes.h5}>
              Organization Bio
            </Typography>

            <Box className={classes.bio}>
              <Markdown allow={["emphasis"]}>{conventionExhibitor.exhibitor_bio}</Markdown>
            </Box>
          </Grid>
        )}
        <Grid item className={classes.btnSection}>
          <ProfileSidebar
            chatAvailable={conventionExhibitor.chat_available?.status ?? "off"}
            exhibitorName={conventionExhibitor.exhibitor_name}
            institutionId={conventionExhibitor.institution_id}
            curator
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <VideosList filter={{ institution_id: conventionExhibitor.institution_id }} />
      </Grid>
      {!!attachments?.data?.length && (
        <Grid item xs={12}>
          <AttachmentsHorizontalScroll
            params={{
              ...attachmentParams,
              fallbackData: attachments.data,
            }}
          />
        </Grid>
      )}
      {!!teamMembers?.data?.length && (
        <Grid item xs={12}>
          <TeamMemberHorizontalScroll
            params={{
              ...attachmentParams,
              fallbackData: teamMembers.data,
            }}
          />
        </Grid>
      )}
    </>
  )
}

export default CuratorBio
